import React from "react";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { pickDisplayPrice } from "../../utils/pricePicker";
import { useCurrency } from "../../components/PriceFormat/PriceFormat";

const AddtoCart = ({ product, selectedColor, customization }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { currency } = useCurrency();

  const norm = (v) => (v ?? "").toString().trim().toLowerCase();

  const variations = product?.color_images || [];
  const defaultImages = product?.default_images || [];

  // ✅ Dil değişse bile stabil: ürün + varyasyon adı (image değil!)
  const makeVariantKey = (p, variationName, customizationObj) => {
    const base = p.stock_code || p.id;
    const v = norm(variationName) || "default";
    const cEnabled = !!customizationObj?.enabled;
    const cText = cEnabled ? norm(customizationObj?.text || "") : "";
    const cPart = cEnabled ? `cust:1:${cText}` : "cust:0";
    return `${base}|${v}|${cPart}`;
  };

  const normalizeCategory = (p) => {
    const c = p?.category;
    if (Array.isArray(c)) return String(c[0] || "").trim() || null;
    if (typeof c === "string") return c.trim() || null;
    return null;
  };

  // selectedColor (Black/Siyah/أسود/...) -> doğru varyasyonu bul
  const resolveVariation = (sel) => {
    const s = norm(sel);
    if (!s || s === "default") return null;

    // 1) Direkt isim eşleşmesi
    let v = variations.find((x) => norm(x.name) === s);
    if (v) return v;

    // 2) Çeviri eşleşmesi
    const langs = Array.from(
      new Set([i18n.language, ...(i18n.languages || []), "en", "tr", "ar"])
    );
    v = variations.find((x) =>
      langs.some((lng) => {
        const a = norm(t(x.name, { lng, defaultValue: "" }));
        const b = norm(t(`colors.${x.name}`, { lng, defaultValue: "" }));
        return (a && a === s) || (b && b === s);
      })
    );
    if (v) return v;

    // 3) Eğer sel URL ise image listesinde ara (opsiyonel)
    const looksUrl = s.startsWith("http://") || s.startsWith("https://");
    if (looksUrl) {
      v = variations.find((x) =>
        (x.images || []).some((img) => norm(img) === s)
      );
      if (v) return v;
    }

    return null;
  };

  const addToCart = () => {
    const found = resolveVariation(selectedColor);

    // ✅ Sepete kaydedilecek varyasyon (name gerçek varyasyon adı olmalı)
    const finalVariation = found
      ? { name: found.name, images: found.images || [] }
      : { name: "Default", images: defaultImages };

    // ✅ ARTIK images'a göre değil, varyasyon adına göre key
    const cleanCustomization = customization?.enabled
      ? { enabled: true, text: (customization.text || "").trim() }
      : { enabled: false, text: "" };

    // Fiyat datasını ürünün içinden aynen sakla (checkout’ta da lazım)
    const customizationDisplayPrices =
      product?.customization?.displayPrices || null;

    const variantKey = makeVariantKey(
      product,
      finalVariation.name,
      cleanCustomization
    );

    const cart = JSON.parse(localStorage.getItem("cart")) || [];

    const existingProductIndex = cart.findIndex((item) => {
      const itemVarName = item.selectedVariation?.name || "Default";
      const itemKey = item.variantKey || makeVariantKey(item, itemVarName);
      return itemKey === variantKey;
    });

    if (existingProductIndex > -1) {
      if ((cart[existingProductIndex].quantity || 1) < 3) {
        cart[existingProductIndex].quantity += 1;

        cart[existingProductIndex].category =
          cart[existingProductIndex].category ?? normalizeCategory(product);

        if (found?.displayPrices) {
          cart[existingProductIndex].displayPrices = found.displayPrices;
        }
        cart[existingProductIndex].priceSnapshot = pickDisplayPrice(
          product,
          currency,
          found?.name || null
        );
      } else {
        Swal.fire({
          icon: "warning",
          title: t("cart.limitReachedTitle"),
          text: t("cart.limitReachedText"),
          confirmButtonText: t("OK"),
        });
        return;
      }
    } else {
      cart.push({
        ...product,
        selectedVariation: finalVariation,
        variantKey,
        quantity: 1,
        category: normalizeCategory(product),

        customization: cleanCustomization.enabled
          ? cleanCustomization
          : undefined,
        customizationDisplayPrices: cleanCustomization.enabled
          ? customizationDisplayPrices
          : null,
        // ✅ varyasyonun displayPrices'ı (Cart'ta doğru hesap için)
        displayPrices: found?.displayPrices || product?.displayPrices || null,

        // ✅ opsiyonel snapshot
        priceSnapshot: pickDisplayPrice(product, currency, found?.name || null),
      });
    }

    localStorage.setItem("cart", JSON.stringify(cart));
    window.dispatchEvent(new Event("cartUpdated"));

    Swal.fire({
      icon: "success",
      title: t("cart.addedTitle", "Product added to cart"),
      toast: true,
      position: "bottom-end",
      showConfirmButton: false,
      timer: 1000,
      timerProgressBar: true,
    }).then(() => navigate("/cart"));
  };

  return (
    <button
      className="btn fw-14 bgcolorfooterbottom w-100 p-2 fw-normal mb-1"
      onClick={addToCart}
    >
      <i className="fa-solid fa-cart-plus fa-1x me-2"></i> {t("Add to Cart")}
    </button>
  );
};

export default AddtoCart;
